import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { canShareImage } from '../lib/share'

/**
 * Sending a bill picture to another app, without falling foul of the rule that
 * breaks the obvious implementation.
 *
 * navigator.share may only be called while the tap that asked for it is still
 * "fresh". Rendering a bill takes a second or three, so doing the work inside
 * the click handler and sharing afterwards gets refused outright:
 *
 *     NotAllowedError: The request is not allowed by the user agent...
 *
 * The way around it is to have the file ready before the tap. So the picture is
 * built quietly in the background as soon as a bill is on screen, and the tap
 * does nothing but hand it over - nothing awaited, nothing for the browser to
 * time out on.
 *
 * The prepared file is thrown away the moment anything about the bill changes.
 * A stale picture would be a wrong bill sent to a customer, which is far worse
 * than a slow one.
 */

export interface BillShare {
  /** This device can hand files to other apps at all - phones can, desktops mostly cannot. */
  supported: boolean
  /** The picture is ready; the next tap sends it immediately. */
  ready: boolean
  /** Being built right now. */
  preparing: boolean
  /** Tapping now sends the bill. */
  share: () => void
  /**
   * The bill has just gone to the share sheet.
   *
   * <p>Used only to confirm it on screen. The message rides along with the
   * picture now, so there is nothing left for the shop to send afterwards.
   */
  sent: boolean
  /** Shown under the button when something went wrong, in plain Urdu. */
  error: string | null
}

/** Long enough for the bill to finish painting before capturing it. */
const SETTLE_MS = 700

/**
 * The greeting line of the message, offered to the share sheet as a title.
 *
 * <p>Some share targets show a title where they ignore the body text, so this
 * is one more chance for the customer's name to travel with the bill. Costs a
 * line; occasionally it is the only wording that arrives.
 */
const firstLine = (caption: string): string => caption.split('\n')[0] ?? ''

/**
 * The file the share sheet is handed: the picture where the device will take
 * one, a PDF where it will not.
 *
 * <p><b>The picture wins because the message travels with it.</b> WhatsApp puts
 * a caption beside a photo and throws it away from a document - so a bill sent
 * as a PDF lands in the chat as a bare file, and the customer is left to work
 * out for himself what it is and what he owes. The bill was sent as a PDF for a
 * while and that is exactly how it behaved: the figures arrived, the sentence
 * did not, and the shop had to send the words again as a second message every
 * single time.
 *
 * <p>A PDF is the better document - crisper when printed, harder to edit and
 * forward with a figure altered, and it does not land among the family photos.
 * None of that outweighs the bill arriving with nothing written next to it. The
 * PDF is still one button away for anyone who wants to save or print one.
 *
 * <p>The fallback is not theoretical: some Android share targets and older
 * WebViews accept one file type and refuse the other, so the type is probed
 * with the real file before the tap, never assumed.
 */
async function buildShareFile(el: HTMLElement, filename: string): Promise<File> {
  const { renderBillFile, renderBillPdfFile } = await import('../lib/pdf')

  const picture = await renderBillFile(el, filename)
  try {
    if (navigator.canShare?.({ files: [picture] })) return picture
  } catch {
    // Some browsers throw rather than answering false; treat that as a no.
  }
  return renderBillPdfFile(el, filename)
}

export function useBillShare({
  target,
  filename,
  caption,
  enabled,
  deps,
}: {
  target: RefObject<HTMLElement | null>
  filename: string
  caption: string
  /** False while there is no bill on screen worth preparing. */
  enabled: boolean
  /** Anything that changes what the bill says. A change discards the file. */
  deps: unknown[]
}): BillShare {
  const supported = useRef(canShareImage()).current
  const [file, setFile] = useState<File | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const captionRef = useRef(caption)
  captionRef.current = caption

  useEffect(() => {
    setFile(null)
    setError(null)
    setSent(false)
    if (!supported || !enabled) return

    let dropped = false
    const timer = window.setTimeout(async () => {
      const el = target.current
      if (!el) return
      setPreparing(true)
      try {
        const made = await buildShareFile(el, filename)
        if (!dropped) setFile(made)
      } catch {
        // Nothing said here: the button still works, it just falls back to
        // building the picture on the tap, and reports any failure then.
      } finally {
        if (!dropped) setPreparing(false)
      }
    }, SETTLE_MS)

    return () => {
      dropped = true
      window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, enabled, filename, ...deps])

  const share = useCallback(() => {
    if (!supported) return
    setError(null)

    // Ready: hand it over inside this very tap, with nothing awaited first.
    if (file) {
      navigator
        .share({ files: [file], text: captionRef.current, title: firstLine(captionRef.current) })
        .then(() => setSent(true))
        .catch((e: Error) => {
          if (e?.name === 'AbortError') return // backing out is not a failure
          setError('واٹس ایپ نہیں کھل سکا۔ دوبارہ کوشش کریں۔')
        })
      return
    }

    // Not ready yet - the tap came before the picture was built. Try anyway; on
    // a short bill it still lands inside the allowed window.
    void (async () => {
      setPreparing(true)
      try {
        const el = target.current
        if (!el) return
        const made = await buildShareFile(el, filename)
        setFile(made)
        await navigator.share({
          files: [made],
          text: captionRef.current,
          title: firstLine(captionRef.current),
        })
        setSent(true)
      } catch (e) {
        const err = e as Error
        if (err?.name === 'AbortError') return
        setError(
          err?.name === 'NotAllowedError'
            ? 'بل تیار ہے — اب دوبارہ بٹن دبائیں تو واٹس ایپ کھل جائے گا۔'
            : 'بل نہیں بن سکا۔ دوبارہ کوشش کریں۔',
        )
      } finally {
        setPreparing(false)
      }
    })()
  }, [supported, file, filename, target])

  return { supported, ready: file !== null, preparing, share, sent, error }
}
